import React, { useEffect, useState } from 'react';
import { Loader } from 'components/atoms/Loader';
import { LETypes } from 'constants/LegalEntityTypes';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { useLang } from 'models/langContext';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { getLegalEntity, getOrgLegalEntity } from 'requests/legalEntities';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import {
  ILegalEntityExtendedAddress,
  ILegalEntityLicenceDetails,
  ILegalEntityLicenceVaryFormValues,
} from 'models/legalEntity';
import LicenceStore from 'state/LicenceStore';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { FormModal } from 'components/molecules/Modal';
import { useTranslations } from 'hooks/useTranslations';
import { useLegalEntities } from '../hooks';
import { LicenceVaryForm } from './licenceVaryForm';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';

export interface IObject {
  [key: string]: boolean;
}

export interface IDocument {
  name: string;
  weaponLicenceDocumentType: string;
  comment: string;
  file: File | null;
}
const initialFormValues: ILegalEntityLicenceVaryFormValues = {
  suitabilityAssessmentPassed: false,
  conditions: {
    weaponNumberLimit: 0,
    goodReasonUniqueIds: null,
    goodReason: '',
    areaOfValidityUniqueId: '',
    competencyUniqueId: '',
    hasSafeStorage: null,
    safeStorageDescription: '',
    safeStorages: [],
    weaponCategorisationUniqueId: '',
    carry: '',
    additionalConditions: '',
  },
  firearm: {
    firearmToLinkUniqueIds: [],
    firearmLinkToBeRemovedUniqueIds: [],
  },
  document: {
    documentToRemoveUniqueIds: [],
    documents: [],
  },
  additionalInformation: '',
  galleryEntryUniqueId: null,
  customFields: [],
  version: '',
};

interface ILicenceVaryEdit {
  id: string;
  caseId: string;
  licenceTemplateId: string;
  licenceDetails: ILegalEntityLicenceDetails;
  backLink: () => void;
  errorType: () => void;
  breadcrumbs: any;
}

const LicenceVaryEdit = ({
  id,
  caseId,
  licenceTemplateId,
  licenceDetails,
  backLink,
  errorType,
  breadcrumbs,
}: ILicenceVaryEdit) => {
  const { selectedLanguage } = useLang();
  const { t } = useTranslations();
  const [formValues, setFormValues] = useState<ILegalEntityLicenceVaryFormValues>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>(null);
  const [accessibleGoodReason, setAccessibleGoodReason] = useState<IDictionary[]>([]);
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>(null);
  const [updateScheduledModal, setUpdateScheduledModal] = useState(false);
  const { getLicence, licence, loadDictionaries, weaponLicenceGoodReason } =
    useConfigurationLicence();
  const { getCaseData, caseData } = useCase();
  const { getLegalEntityAddressesData, legalEntityAddress } = useLegalEntities();
  const [primaryAddress, setPrimaryAddress] = useState<ILegalEntityExtendedAddress>();

  useEffect(() => {
    (async () => {
      await getCaseData(caseId);
    })();
  }, [caseId]);

  useEffect(() => {
    if (licenceDetails?.uniqueId) {
      LicenceStore.prepareForNewOperation(licenceDetails.uniqueId);
    }
  }, [licenceDetails?.uniqueId]);

  const saveToStory = (formData: any) => {
    LicenceStore.setFormData(formData, licenceDetails.uniqueId);
  };

  useEffect(() => {
    (async function getLegalEntityAddressInit() {
      if (id) {
        await getLegalEntityAddressesData(id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const address = legalEntityAddress.filter((item) => item.isDefault);
    if (address && address.length) {
      setPrimaryAddress(address[0]);
    } else {
      setPrimaryAddress(undefined);
    }
  }, [legalEntityAddress]);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.weaponLicenceGoodReason]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function getDataInit() {
      setIsLoading(true);
      if (licenceTemplateId && id) {
        await getLicence(licenceTemplateId);
      }
      setIsLoading(false);
    })();
  }, [licenceTemplateId]);

  useEffect(() => {
    if (caseData) {
      if (caseData.legalType === LETypes.individual) {
        (async function getLegalEntityDataInit() {
          setIsLoading(true);
          if (id) {
            const response = await getLegalEntity(id);
            if (response) {
              setLegalEntity(response);
            }
            setIsLoading(false);
          }
        })();
        setOrgLegalEntity(null);
      } else if (caseData.legalType === LETypes.organization) {
        (async function getLegalEntityDataInit() {
          setIsLoading(true);
          if (id) {
            const response = await getOrgLegalEntity(id);
            if (response) {
              setOrgLegalEntity(response);
            }
            setIsLoading(false);
          }
        })();
        setLegalEntity(null);
      } else {
        errorType();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, caseData]);

  const prepareForm = (goodReasonUniqueIds: any) => {
    const data: ILegalEntityLicenceVaryFormValues = { ...initialFormValues };
    data.conditions = {
      ...initialFormValues.conditions,
      goodReasonUniqueIds: licence?.conditions.goodReasonDictionaryIds.length
        ? goodReasonUniqueIds
        : null,
      areaOfValidityUniqueId: licence?.conditions.specificCountryArea ? '1' : '',
    };
    return data.conditions;
  };

  useEffect(() => {
    if (!formValues && weaponLicenceGoodReason?.length && licence && licenceDetails) {
      const dataFromStore = LicenceStore.getFormData(licenceDetails.uniqueId);

      if (dataFromStore) {
        setFormValues(dataFromStore);
      } else {
        LicenceStore.setLicenceId(licenceDetails.uniqueId);
        let goodReasonUniqueIds = null;
        if (licence.conditions.goodReasonDictionaryIds.length) {
          const accessibleWeaponLicenceGoodReason = weaponLicenceGoodReason.filter((item) =>
            licence.conditions.goodReasonDictionaryIds.includes(item.uniqueId.toString()),
          );
          setAccessibleGoodReason(accessibleWeaponLicenceGoodReason);
          goodReasonUniqueIds = accessibleWeaponLicenceGoodReason.reduce(
            (acc, item) => {
              acc[item.uniqueId.toString()] = false;
              return acc;
            },
            {} as Record<string, boolean>,
          );
        }

        const dataConditionsForm = prepareForm(goodReasonUniqueIds);

        const newFormData = {
          ...initialFormValues,
          firearm: {
            ...initialFormValues.firearm,
            ...licenceDetails.firearm,
          },
          conditions: {
            ...initialFormValues.conditions,
            ...dataConditionsForm,
          },
          additionalInformation: licenceDetails.additionalInformation ?? null,
          version: licenceDetails.version,
        };

        setFormValues(newFormData);
        saveToStory(newFormData);
      }
    }
  }, [weaponLicenceGoodReason, licence, licenceDetails]);

  return (
    <div>
      <FormModal
        open={updateScheduledModal}
        onClickClose={() => {
          setUpdateScheduledModal(false);
          backLink();
        }}
        title={t('legalEntity.licenceDetails.scheduled.modal.title')}
        text={t('legalEntity.licenceDetails.scheduled.modal.text')}
        cancelButtonLabel="general.ok.label"
        titleLeft
      />
      {licence &&
      caseData &&
      formValues &&
      ((caseData.legalType === LETypes.individual && legalEntity) ||
        (caseData.legalType === LETypes.organization && orgLegalEntity)) &&
      !isLoading ? (
        <LicenceVaryForm
          licence={licence}
          caseData={caseData}
          legalEntityLicenceDetails={licenceDetails}
          legalEntity={
            caseData.legalType === LETypes.individual ? legalEntity : orgLegalEntity
          }
          type={caseData.legalType as LETypes}
          initialFormValues={formValues}
          primaryAddress={primaryAddress}
          accessibleWeaponLicenceGoodReason={accessibleGoodReason}
          availableAddresses={legalEntityAddress}
          backLink={backLink}
          setUpdateScheduledModal={() => setUpdateScheduledModal(true)}
          breadcrumbs={breadcrumbs}
          saveToStory={saveToStory}
        />
      ) : (
        <Loader isLoading />
      )}
    </div>
  );
};

export { LicenceVaryEdit };
